import { CardWrapper } from './card-wrapper';
import {
  CakeIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
  HomeIcon,
  PhoneIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { GenderIcon, toDateFormat } from '@restworld/utils-common';
import { Alert } from '@restworld/ui-ds-base';
import { PresentationCard } from '@restworld/ui-components';
import { atom, useAtomValue } from 'jotai';
import { workerPersonalDetailsAtom } from '../global-state.worker-data';
import { WorkerDataLoader } from './worker-data-loader';
import { useTranslation } from 'next-i18next';
import { useFetchWorkerDataLoading } from '../../hooks';

const fullNameAtom = atom((get) => {
  const personalDetails = get(workerPersonalDetailsAtom);
  return personalDetails?.name || personalDetails?.surname
    ? `${personalDetails?.name || ''} ${personalDetails?.surname || ''}`
    : undefined;
});

const birthDateAtom = atom((get) => {
  const personalDetails = get(workerPersonalDetailsAtom);
  return personalDetails?.birthdate ? toDateFormat(personalDetails.birthdate, 'dd/MM/yyyy') : undefined;
});

const communicationPreferencesAtom = atom((get) => {
  const personalDetails = get(workerPersonalDetailsAtom);
  const communicationPreferences =
    personalDetails?.communication_channel_preferences
      ?.map((preference) => preference?.communication_channel_preference_var?.label)
      ?.filter((preference) => preference) || [];

  if (personalDetails?.other_communication_channel_preference) {
    communicationPreferences.push(personalDetails.other_communication_channel_preference);
  }

  const summaryCommunicationPreference =
    communicationPreferences.length > 0 ? communicationPreferences.join(', ') : undefined;

  return summaryCommunicationPreference;
});
export const MyProfileSection = () => {
  const { t } = useTranslation();
  const personalDetails = useAtomValue(workerPersonalDetailsAtom);
  const workerDataLoading = useFetchWorkerDataLoading();
  const name = useAtomValue(fullNameAtom);
  const birthDate = useAtomValue(birthDateAtom);
  const summaryCommunicationPreference = useAtomValue(communicationPreferencesAtom);
  const { isPersonalInfoMissing: isInformationMissing } = useIsWorkerPersonalInfoMissing();

  const errorMessage = t('common:missing_info');

  return (
    <CardWrapper
      title={t('onboarding-page:on_you')}
      editPageType="personal_info"
      showAlert={isInformationMissing && !workerDataLoading}
    >
      <div id="personal_info" data-oo-privacy="mask" className="flex flex-col items-start pt-4">
        {workerDataLoading && <WorkerDataLoader />}
        {isInformationMissing && !workerDataLoading && (
          <Alert text={t('onboarding-page:missing_info_fill_fields')} className="mb-4" />
        )}
        {!workerDataLoading && (
          <div className="flex lg:flex-row flex-col lg:gap-8 gap-4 w-full">
            <div className="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
              <PresentationCard
                label={t('onboarding-page:full_name')}
                header={name || errorMessage}
                icon={<UserIcon className="w-6 h-6" />}
                isError={!name}
              />
              <PresentationCard
                label={t('onboarding-page:gender')}
                header={personalDetails?.sex || errorMessage}
                isError={!personalDetails?.sex}
                icon={<GenderIcon />}
              />
              <PresentationCard
                label={t('onboarding-page:birth_date')}
                header={birthDate || errorMessage}
                isError={!birthDate}
                icon={<CakeIcon className="w-6 h-6" />}
              />
              <PresentationCard
                label={t('onboarding-page:address')}
                header={personalDetails?.address || errorMessage}
                isError={!personalDetails?.address}
                icon={<HomeIcon className="w-6 h-6" />}
              />
            </div>
            <div className="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
              <PresentationCard
                label={t('onboarding-page:email_address')}
                header={personalDetails?.email || errorMessage}
                isError={!personalDetails?.email}
                icon={<EnvelopeIcon className="w-6 h-6" />}
              />

              <PresentationCard
                label={t('onboarding-page:phone_number')}
                header={personalDetails?.phone_number || errorMessage}
                isError={!personalDetails?.phone_number}
                icon={<PhoneIcon className="w-6 h-6" />}
              />

              <PresentationCard
                label={t('onboarding-page:communication_preferences')}
                header={summaryCommunicationPreference || errorMessage}
                isError={!summaryCommunicationPreference}
                icon={<ChatBubbleOvalLeftEllipsisIcon className="w-6 h-6" />}
              />
            </div>
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

export function useIsWorkerPersonalInfoMissing() {
  const personalDetails = useAtomValue(workerPersonalDetailsAtom);
  const name = useAtomValue(fullNameAtom);
  const birthDate = useAtomValue(birthDateAtom);
  const summaryCommunicationPreference = useAtomValue(communicationPreferencesAtom);

  const isPersonalInfoMissing =
    !name ||
    !birthDate ||
    !summaryCommunicationPreference ||
    !personalDetails?.sex ||
    !personalDetails?.address ||
    !personalDetails?.email ||
    !personalDetails?.phone_number;

  return { isPersonalInfoMissing };
}
